import DOMPurify from 'dompurify';

const SIGNATURE_PREFIX = '<div>--</div>';
const SIGNATURE_BREAK = '<div><br></div>';
const MAX_INTERACTION_BODY_LENGTH = 9500;

// <br> is the only tag parseBodyForInteraction ever emits, so a length cutoff can only
// ever land inside that literal 4-character token — back up out of it rather than leaving
// a stray "<br" or "<b" fragment in the stored interaction text. DOMPurify also encodes
// plain text characters like &, <, > into HTML entities (e.g. "&" -> "&amp;"), so the same
// cutoff can just as easily land inside one of those — back up out of a partial entity too.
const truncateHtmlSafely = (html, maxLength) => {
  if (html.length <= maxLength) return html;
  const cutoff = html.slice(0, maxLength);
  const partialFragment = cutoff.match(/<b?r?$|&#?[a-zA-Z0-9]*$/);
  return partialFragment ? cutoff.slice(0, partialFragment.index) : cutoff;
};

export const useFormatEmailBody = () => {
  const removeParagraphTags = (body) => {
    return body.replace(/<p[^>]*>/g, '<div>').replace(/<\/p>/g, '</div>');
  };

  const getFormattedSignature = (signature) => {
    const strippedSignature = removeParagraphTags(signature.signature_body);
    const signatureWithId = strippedSignature.replace(/<div>/, `<div data-signature-id="${signature.id}">`);
    return `${SIGNATURE_BREAK}${SIGNATURE_PREFIX}${signatureWithId}`;
  };

  const parseBodyForExisitingSignature = (body, signature) => {
    const formattedSignature = getFormattedSignature(signature);
    const signaturePrefixIndex = body.indexOf(SIGNATURE_PREFIX);
    const parsedBody = body.substring(0, signaturePrefixIndex);

    if (signature.id === 0) return parsedBody;

    if (signaturePrefixIndex === -1) {
      return body + formattedSignature;
    }

    if (parsedBody === SIGNATURE_BREAK) {
      return formattedSignature;
    }

    return parsedBody + formattedSignature;
  };

  const updateBodyWithTemplate = (body, templateBody, templateId) => {
    const signaturePrefixIndex = body.indexOf(SIGNATURE_PREFIX);
    const strippedTemplateBody = removeParagraphTags(templateBody);

    // Add template id as a data attribute to the first div
    const templateWithId = strippedTemplateBody.replace(/<div>/, `<div data-template-id="${templateId}">`);

    if (signaturePrefixIndex === -1) {
      return body + templateWithId;
    } else {
      const beforeSignature = body.substring(0, signaturePrefixIndex);
      const signaturePart = body.substring(signaturePrefixIndex);

      if (beforeSignature === SIGNATURE_BREAK) {
        return templateWithId + signaturePart;
      }

      return beforeSignature + templateWithId + signaturePart;
    }
  };

  const hasSignatureOnly = (body) => {
    const signaturePrefixIndex = body.indexOf(SIGNATURE_PREFIX);
    const bodyWithoutSignature = signaturePrefixIndex === -1 ? body : body.substring(0, signaturePrefixIndex);
    const bodyWithoutBreaks = bodyWithoutSignature.replaceAll(SIGNATURE_BREAK, '');
    return bodyWithoutBreaks.trim().length === 0;
  };

  // Keeps the RTE's line breaks as HTML (<br>) instead of flattening to plain text, so the
  // interaction's rich-text renderer can still display the email's paragraph structure.
  const parseBodyForInteraction = (body) => {
    const withBreaks = body.replaceAll(/<br\s*\/?>/g, '<br>').replaceAll(/<div[^>]*>/g, '<br>');

    // DOMPurify parses the markup into a real DOM tree rather than reasoning about it as
    // text, so nested/malformed tags (e.g. "<scr<script>ipt>") can't reassemble into a tag
    // that survives a single pass, which is what a hand-rolled tag-strip regex is prone to.
    // Wrapping in a container tag forces DOMPurify to always take that parse/serialize path —
    // otherwise tag-free input takes a fast path that skips entity escaping (e.g. a literal
    // NBSP character would survive instead of being escaped to "&nbsp;").
    const sanitized = DOMPurify.sanitize(`<div>${withBreaks}</div>`, { ALLOWED_TAGS: ['br'], ALLOWED_ATTR: [] });
    const withOnlyBreakTags = sanitized.replaceAll(/&nbsp;/g, ' ');

    const truncated = truncateHtmlSafely(withOnlyBreakTags, MAX_INTERACTION_BODY_LENGTH);

    return truncated.replace(/^(?:\s|<br>)+/, '').replace(/(?:\s|<br>)+$/, '');
  };

  return {
    parseBodyForExisitingSignature,
    getFormattedSignature,
    updateBodyWithTemplate,
    removeParagraphTags,
    hasSignatureOnly,
    parseBodyForInteraction,
  };
};

export default useFormatEmailBody;
